import React from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Box, Typography, Card, CardContent, Grid } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TemporalAnalysisChart = ({ monthlyData, title = "Análise Temporal Detalhada", dashboardData }) => {
  if (!monthlyData || monthlyData.length === 0) {
    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={300}>
            <Typography color="text.secondary">
              Não há dados disponíveis para exibir
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Dados para gráfico de linha temporal - Evolução mensal
  const monthlyEvolutionData = {
    labels: monthlyData.map(item => `${item.month_name} ${item.year}`),
    datasets: [
      {
        label: 'Volume de Vendas',
        data: monthlyData.map(item => item.total_sales),
        borderColor: 'rgba(69, 179, 209, 1)',
        backgroundColor: 'rgba(69, 179, 209, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Receita (R$)',
        data: monthlyData.map(item => parseFloat(item.total_revenue)),
        borderColor: 'rgba(150, 206, 180, 1)',
        backgroundColor: 'rgba(150, 206, 180, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  // Dados para gráfico de ticket médio
  const ticketEvolutionData = {
    labels: monthlyData.map(item => `${item.month_name} ${item.year}`),
    datasets: [
      {
        label: 'Ticket Médio (R$)',
        data: monthlyData.map(item => parseFloat(item.average_ticket)),
        borderColor: 'rgba(255, 107, 107, 1)',
        backgroundColor: 'rgba(255, 107, 107, 0.2)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Performance por dia da semana baseada nos dados reais
  const calculateWeeklyPerformance = (monthlyData) => {
    // Normaliza diferentes formatos de nomes de dias para rótulos curtos PT-BR
    const normalizeDay = (name) => {
      if (!name) return 'N/A';
      const map = {
        // PT-BR completo
        'domingo': 'Dom', 'segunda': 'Seg', 'segunda-feira': 'Seg', 'terca': 'Ter', 'terça': 'Ter', 'terça-feira': 'Ter',
        'quarta': 'Qua', 'quarta-feira': 'Qua', 'quinta': 'Qui', 'quinta-feira': 'Qui', 'sexta': 'Sex', 'sexta-feira': 'Sex',
        'sabado': 'Sáb', 'sábado': 'Sáb',
        // PT-BR curto
        'dom': 'Dom', 'seg': 'Seg', 'ter': 'Ter', 'qua': 'Qua', 'qui': 'Qui', 'sex': 'Sex', 'sáb': 'Sáb', 'sab': 'Sáb',
        // EN-US
        'sunday': 'Dom', 'monday': 'Seg', 'tuesday': 'Ter', 'wednesday': 'Qua', 'thursday': 'Qui', 'friday': 'Sex', 'saturday': 'Sáb'
      };
      const key = String(name).trim().toLowerCase();
      return map[key] || name; // se já estiver no formato curto, mantém
    };

    const dayOfWeekStats = {
      'Dom': 0, 'Seg': 0, 'Ter': 0, 'Qua': 0, 'Qui': 0, 'Sex': 0, 'Sáb': 0
    };
    
    monthlyData.forEach(month => {
      if (month.daily_breakdown && Array.isArray(month.daily_breakdown)) {
        month.daily_breakdown.forEach(day => {
          const dayName = normalizeDay(day.day_of_week || '');
          if (dayOfWeekStats.hasOwnProperty(dayName)) {
            dayOfWeekStats[dayName] += day.total_sales || 0;
          }
        });
      }
    });
    
    return dayOfWeekStats;
  };

  const weeklyStats = calculateWeeklyPerformance(monthlyData);
  const weeklyPerformanceData = {
    labels: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    datasets: [
      {
        label: 'Vendas por Dia da Semana',
        data: [
          weeklyStats['Dom'],
          weeklyStats['Seg'],
          weeklyStats['Ter'],
          weeklyStats['Qua'],
          weeklyStats['Qui'],
          weeklyStats['Sex'],
          weeklyStats['Sáb']
        ],
        backgroundColor: [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Usar dados do backend se disponível, senão calcular localmente
  const weeklyData = dashboardData?.weekly_patterns || [];
  
  // Debug: log dos dados do backend
  console.log('Weekly patterns from backend:', weeklyData);
  
  // Se não há dados do backend ou está vazio, usar dados calculados localmente
  const shouldUseBackendData = weeklyData.length > 0;
  
  console.log('Should use backend data:', shouldUseBackendData);
  
  let finalBarChartData;
  
  if (shouldUseBackendData) {
    // Dados normalizados do backend
    finalBarChartData = {
      labels: weeklyData.map(w => w.day_of_week),
      datasets: [
        {
          label: 'Média de Vendas por Dia da Semana (Normalizada)',
          data: weeklyData.map(w => w.total_sales),
          backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF'],
        }
      ]
    };
  } else {
    // Fallback: usar dados calculados localmente
    finalBarChartData = weeklyPerformanceData;
  }

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset?.label || '';
            const value = context.parsed?.y || 0;
            
            if (label.includes('Receita') || label.includes('Ticket')) {
              return `${label}: R$ ${value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
            }
            return `${label}: ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Período',
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Volume de Vendas',
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString('pt-BR');
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Receita (R$)',
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function(value) {
            return `R$ ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
  };

  const singleAxisOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: R$ ${value.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Período',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Ticket Médio (R$)',
        },
        ticks: {
          callback: function(value) {
            return `R$ ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Dia da Semana',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Volume de Vendas',
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString('pt-BR');
          },
        },
      },
    },
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        {title}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Gráfico de Evolução Temporal Geral */}
        <Grid item xs={12}>
          <Card sx={{ height: 500 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumo Geral - Análise Temporal Completa
              </Typography>
              <Box height={420}>
                <Line data={monthlyEvolutionData} options={lineOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gráfico de Evolução do Ticket Médio */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Evolução do Ticket Médio Diário
              </Typography>
              <Box height={320}>
                <Line data={ticketEvolutionData} options={singleAxisOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance por Dia da Semana */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vendas por Dia da Semana
              </Typography>
              <Box height={320}>
                <Bar data={finalBarChartData} options={barOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemporalAnalysisChart;
